import OlTile from 'ol/layer/Tile';
import OlTileWMS from 'ol/source/TileWMS';
import OlImage from 'ol/layer/Image';
import OlImageWMS from 'ol/source/ImageWMS';

import { ogcCreateFilterValues, opengisNamespace } from '../filter';
import { getRequestUrl } from '../';
import { ngApiSecuredTileLoadFunction, ngApiHighQualityTileLoadFunction } from '../wms';

/**
* [createWfsWmsLayerObject description]
* @param  {object} store        pointer to vuex store
* @param  {object} layerData    properties of the layer
* @param  {string} url          url of the layer (should be refactored to be created inside function from layerData)
* @param  {array} extent        bbox of the current view
* @param  {array} filters       array of filters (should be refactored to be read inside function from layerData?)
* @param  {boolean} highQuality flag to render layer via proxy and larger tiles (for printing)
*/
export const createWfsWmsLayerObject = function (store, layerData, extent, filters, highQuality) {
    let styleName = '';
    let version = '1.3.0';

    // never use a token from web since it is not available
    const requestWithToken = !store.getters.isWeb && layerData.isSecured;

    // allways use custom endpoint for high quality (cors)
    const url = highQuality ? layerData.customEndPoints.wms.url : getRequestUrl(layerData, 'WMS');

    const firstStyle = layerData.styles?.[0];

    if (firstStyle) {
        styleName = firstStyle.name;
    }
    if (layerData.owsVersionWms < 1.3 && styleName === '' && layerData.owsVersionWfs !== 0) {
        styleName = layerData.layerName;
        version = '1.1.1';
    }

    // create common properties
    const commonLayerProperties = {
        title: layerData.title,
        id: layerData.id,
        combinedId: `${layerData.getCombinedOlId()}-WMS`,
        optional: layerData.layerName,
        type: 'WMS',
        opacity: 1,
    };

    // create common paramaters
    const commonNonFeedbackTileLayerParameters = {
        LAYERS: layerData.graphicalLayerName,
        TILED: layerData.tiledRequest,
        // Add empty string if styleName is null to force STYLE= parameter into request URL
        STYLES: styleName ?? '',
        tilesorigin: `${extent[0]},${extent[1]}`,
        VERSION: version
    };

    // Use ImageLayer for storagelayers (singleTile for performance or cluster/heatmap display)
    if (layerData.feedbackId) {
        const CQLFilters = getLayerCQLFilters(filters);
        return new OlImage({
            ...commonLayerProperties,
            source: new OlImageWMS({
                url: url,
                crossOrigin: highQuality ? 'Anonymous' : undefined,
                imageLoadFunction: ngApiSecuredTileLoadFunction(store, layerData),
                params: {
                    ...commonNonFeedbackTileLayerParameters,
                    'CQL_FILTER': CQLFilters
                },
                ratio: 1,
                serverType: 'geoserver'
            })
        });
    }
    // Use TileWMS
    let params;
    if (layerData.source.hasOws) {
        const CQLFilters = getLayerCQLFilters(filters);
        params = {
            ...commonNonFeedbackTileLayerParameters,
            'CQL_FILTER': CQLFilters
        };
    } else {
        const filterQuery = getLayerOGCFilters(layerData, filters);
        const OgcFilters = getOgcFilter(filterQuery);
        params = {
            ...commonNonFeedbackTileLayerParameters,
            'FILTER': OgcFilters
        };
    }
    const highQualityLoadFunction = highQuality ? ngApiHighQualityTileLoadFunction(highQuality) : undefined;
    const source = new OlTileWMS({
        crossOrigin: highQuality ? 'Anonymous' : undefined,
        url: url,
        transition: 0,
        params,
        tileLoadFunction: requestWithToken ? ngApiSecuredTileLoadFunction(store, layerData) : highQualityLoadFunction
    });
    return new OlTile({
        ...commonLayerProperties,
        visible: !layerData.baselayer,
        source
    });
};

export function getLayerOGCFilters(layerData, filters) {
    const serviceType = layerData.serviceType.toUpperCase();
    let sourceVersion = 1.1;
    if (serviceType === 'WFS') {
        sourceVersion = layerData.owsVersionWfs;
    } else if (serviceType === 'WMS') {
        sourceVersion = layerData.owsVersionWms;
    }
    // get the correct namespace for the version
    const createdOpengisNamespace = opengisNamespace(sourceVersion);
    // initialize to a empty filter
    let filterQuery = '';
    // OGC filters for WFS and WMS layers
    if (serviceType === 'WFS' || serviceType === 'WMS') {
        // <Filter> tag
        const ogcFilter = document.createElementNS(createdOpengisNamespace, 'Filter');
        // <And> tag
        const ogcAnd = document.createElementNS(createdOpengisNamespace, 'And');
        // lijst met attribuutfilters
        if (filters.length > 1) {
            // Loop over filters (multiple are possible)
            ogcCreateFilterValues(sourceVersion, filters, ogcAnd);
            ogcFilter.appendChild(ogcAnd);
            // filter with multiple attribute-value pairs
            filterQuery = new XMLSerializer().serializeToString(ogcFilter);
        } else if (filters.length > 0) {
            ogcCreateFilterValues(sourceVersion, filters, ogcFilter);
            // filter with a single attribute-value pair
            filterQuery = new XMLSerializer().serializeToString(ogcFilter);
        }
    }
    return filterQuery;
}

function getOgcFilter(filterQuery) {
    // Get decoded cql filter
    if (filterQuery && filterQuery.length > 0) {
        return filterQuery;
    }
    // OGC filter is null
    return null;
}

function getLayerCQLFilters(filters) {
    // filters query
    // Process each filter
    // Concatenate the CQL filters
    if (filters?.length) {
        return filters.map(filter => cqlGetFilter(filter)).join(' AND ');
    }
    return undefined;
}

function cqlGetFilter(filter) {
    // zet de datum om naar het juiste formaat
    filter.setDateValue({ fromFormat: 'dd-mm-yyyy' });
    // Check for regular of null/empty string filter
    if (filter.value === '' || filter.value === null) {
        return cqlCreateNullOrEmtpyFilter(filter);
    } else {
        // Is it an array of filter values
        if (filter.value instanceof Array) {
            if (filter.operator === 'C') {
                return cqlCreateInListFilter(filter);
            } else {
                return cqlCreateEqualListFilter(filter);
            }

        } else {
            return cqlCreateRegularFilter(filter);
        }
    }
}

function cqlCreateNullOrEmtpyFilter(filter) {
    // Follow theme/detail/controller
    // possibleoperators: ['=', '<>', '>', '>=', '<', '<=', 'bevat', 'periode'];
    switch (filter.operator) {
        // Assume Not equals, fall through
        case '<>':
        case '<':
        case '>':
            return `(${filter.filterAttribute} IS NOT NULL AND ${filter.filterAttribute}  <> '')`;
        // Assume Equals, fall through
        case 'C':
        case 'like':
        case '>=':
        case '<=':
        case '=':
        default:
            if (filter.dataType.indexOf('string') > -1) {
                return `(${filter.filterAttribute} IS NULL OR ${filter.filterAttribute}  = '')`;
            }
            return `${filter.filterAttribute} IS NULL`;
    }
}

/**
* Create CQL Filter string for a non empty filter value
* @param {object} filter - filter object
* @returns {string} a single CQL-filter for a non empty filter value
*/
function cqlCreateRegularFilter(filter) {
    // Handle case sensitivity
    // Compare lower case values if case sensitivity is false
    const isDateField = filter.dataType.indexOf('date') > -1;
    const filterAttribute = (filter.caseSensitive || isDateField)
        ? filter.filterAttribute
        : `strToLowerCase(${filter.filterAttribute})`;
    const filterValue = (filter.caseSensitive || isDateField)
        ? filter.value
        : filter.value.toLowerCase();
    // Follow theme/detail/controller
    // possibleoperators: ['=', '<>', '>', '>=', '<', '<=', 'bevat', 'periode','Start met','Eindigt op'];
    switch (filter.operator) {
        case 'C':
        case 'like':
            return `${filterAttribute} LIKE '%${filterValue}%'`;
        case 'S':
            return `${filterAttribute} LIKE '${filterValue}%'`;
        case 'E':
            return `${filterAttribute} LIKE '%${filterValue}'`;
        case '<>':
            return `${filterAttribute} <> '${filterValue}'`;
        case '>':
            return `${filterAttribute} > '${filterValue}'`;
        case '>=':
            return `${filterAttribute} >= '${filterValue}'`;
        case '<':
            return `${filterAttribute} < '${filterValue}'`;
        case '<=':
            return `${filterAttribute} <= '${filterValue}'`;
        case '=':
        // Fall through
        default:
            return `${filterAttribute} = '${filterValue}'`;
    }
}

/**
* Create CQL Filter string for a IN value list
* @param {object} filter - filter object
* @returns {string} a single CQL-filter for a non empty filter value
*/
function cqlCreateInListFilter(filter) {
    let filterQuery = '';
    for (let value of filter.value) {
        if (filterQuery.length > 0) {
            // add operator
            filterQuery = filterQuery.concat(' OR ');
        }
        // add operand
        const partialFilter = `${filter.filterAttribute} LIKE '%${value}%'`;
        filterQuery = filterQuery.concat(partialFilter);
    }
    return filterQuery;
}

/**
* Create CQL Filter string for a IN value list
* @param {object} filter - filter object
* @returns {string} a single CQL-filter for a non empty filter value
*/
function cqlCreateEqualListFilter(filter) {
    return `${filter.filterAttribute} IN ('${filter.value.join('\' ,\'')}')`;
}
